/**
 * CreateManualAmcDialog (Iter 28)
 *
 * Direct / Manual AMC creation against an existing Client. Used for legacy or
 * migrated relationships that never flowed through Proposal → PO.
 *
 * Iter 28: line items are now the commercial source of truth. Users can
 *   - "Add from catalogue"  → searchable picker over Products & Services
 *   - "Add custom line"     → free-form row
 * Each line carries its own qty, rate, GST %, HSN/SAC; mixed GST is allowed.
 * Catalogue items are SNAPSHOTTED on the schedule at create time, so future
 * catalogue edits never mutate historical AMCs (data immutability principle).
 *
 * Restricted to super_admin (Iter 27.1) — gated at the AMC page level.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Repeat, Buildings, Calendar, Trash, Plus, MagnifyingGlass, Receipt, FileText } from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { DialogFooter } from "./ui/dialog";
import CurrencySelect from "./CurrencySelect";
import FormDialog from "./FormDialog";
import CataloguePickerDialog from "./CataloguePickerDialog";
import { toast } from "sonner";
import { annualPeriodEndIso } from "../lib/servicePeriod";

const FREQUENCIES = ["Annual", "Half-Yearly", "Quarterly", "Monthly"];
const GST_RATES = [0, 5, 12, 18, 28];

let _lineKeyCounter = 0;
const newKey = () => `line-${++_lineKeyCounter}-${Date.now()}`;

const blankLine = (overrides = {}) => ({
    _key: newKey(),
    catalogue_id: null,
    product_name: "",
    description: "",
    quantity: 1,
    rate: "",
    gst_percent: 18,
    hsn_sac: "997331",
    ...overrides,
});

const blankForm = () => ({
    client_id: "",
    amc_name: "",
    start_date: "",
    end_date: "",
    billing_frequency: "Annual",
    billing_day: "",
    notes: "",
    external_po_number: "",
    external_po_date: "",
    proposal_number: "",  // Iter 51.0.2 — optional legacy REEDOS Proposal No (manual AMCs only)
    currency: "INR",  // Iter 49.2 — denomination set at create time, immutable
    line_items: [blankLine()],
});

const formatINR = (n, currency = "INR") => {
    const num = Number(n) || 0;
    // Iter 49.2 — currency-aware local helper. Falls back to INR when the
    // provided code isn't in the supported set (defensive).
    const locales = { INR: "en-IN", USD: "en-US", AED: "en-AE", GBP: "en-GB", EUR: "de-DE" };
    const safeCode = locales[currency] ? currency : "INR";
    return new Intl.NumberFormat(locales[safeCode], {
        style: "currency", currency: safeCode, maximumFractionDigits: 2,
    }).format(num);
};

export default function CreateManualAmcDialog({ open, onOpenChange, onCreated, defaultClientId }) {
    const [form, setForm] = useState(blankForm());
    const [clients, setClients] = useState([]);
    const [catalogue, setCatalogue] = useState([]);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [pickerLineKey, setPickerLineKey] = useState(null);
    const [busy, setBusy] = useState(false);
    const baselineRef = useRef(blankForm());
    // Iter 165b (2026-10-14) — optional PO item fetch. Unlinked copy: the
    // manual AMC stays po_id-less and the PO is never modified.
    const [pos, setPos] = useState([]);
    const [poFetchId, setPoFetchId] = useState("");
    const [poFetching, setPoFetching] = useState(false);
    // Iter E — subscription-end derivation over the PO-fetched rows.
    const [datesTouched, setDatesTouched] = useState(false);

    useEffect(() => {
        if (!open) return;
        const next = { ...blankForm(), client_id: defaultClientId || "" };
        setForm(next);
        baselineRef.current = next;
        // Pull clients + catalogue masters in parallel
        Promise.all([
            api.get("/billing/client/list"),
            api.get("/billing/catalogue/list"),
            api.get("/billing/po/list", { params: { status: "finalized" } }),
        ])
            .then(([cRes, catRes, poRes]) => {
                setClients(cRes.data.items || []);
                setCatalogue(catRes.data.items || []);
                const allPos = poRes.data.items || [];
                // Only POs carrying AMC value can supply recurring items —
                // keeps the dropdown short and purposeful (fallback: all).
                const withAmc = allPos.filter((p) => p.billing_status?.has_amc_value);
                setPos(withAmc.length ? withAmc : allPos);
            })
            .catch(() => { /* silent — masters are best-effort */ });
    }, [open, defaultClientId]);

    const isDirty = useMemo(
        () => JSON.stringify(form) !== JSON.stringify(baselineRef.current),
        [form],
    );

    const set = (patch) => setForm((f) => ({ ...f, ...patch }));

    const updateLine = (key, patch) =>
        setForm((f) => ({ ...f, line_items: f.line_items.map((l) => (l._key === key ? { ...l, ...patch } : l)) }));

    const removeLine = (key) =>
        setForm((f) => ({ ...f, line_items: f.line_items.filter((l) => l._key !== key) }));

    const addCustomLine = () =>
        setForm((f) => ({ ...f, line_items: [...f.line_items, blankLine()] }));

    const openCatalogue = (key) => {
        setPickerLineKey(key);
        setPickerOpen(true);
    };

    /**
     * Multi-select commit handler (2026-02-13).
     *
     * Smart-fill semantics preserved verbatim from the legacy single-tap
     * picker for the COMMON case (one blank starter line):
     *   • If the picker was opened from a line that's still empty, fill
     *     it with item #1 and append items #2..N as fresh lines.
     *   • Otherwise (picker opened from a populated line, OR opened via
     *     the "+ Browse catalogue" header button), append every item as
     *     a new line.
     */
    const commitCatalogueSelection = (items) => {
        if (!items?.length) return;
        const filled = items.map((item) => ({
            catalogue_id: item.id,
            product_name: item.name,
            rate: item.default_amc_fee || "",
            gst_percent: item.default_gst_percent ?? 18,
            // Iter 38 — auto-fill HSN/SAC from catalogue master.
            hsn_sac: item.hsn_sac_code || "997331",
        }));
        setForm((f) => {
            const lines = [...f.line_items];
            const targetIdx = pickerLineKey
                ? lines.findIndex((l) => l._key === pickerLineKey)
                : -1;
            // Smart-fill: when the picker was opened from a still-empty line,
            // reuse that slot for the first selected item.
            if (targetIdx >= 0 && !lines[targetIdx].product_name) {
                lines[targetIdx] = { ...lines[targetIdx], ...filled[0] };
                for (let i = 1; i < filled.length; i++) {
                    lines.push(blankLine(filled[i]));
                }
            } else {
                for (const f0 of filled) {
                    lines.push(blankLine(f0));
                }
            }
            return { ...f, line_items: lines };
        });
        toast.success(
            `${filled.length} product${filled.length === 1 ? "" : "s"} added to AMC schedule`,
        );
        setPickerOpen(false);
        setPickerLineKey(null);
    };

    /**
     * Iter 165b — Fetch a PO's recurring items into the line table.
     * Rates come from the PO's AMC components only — setup / one-time
     * prices are never substituted. Blank-rated items arrive with an empty
     * rate — like a proposal scope line, a blank rate is Included (₹0).
     * Items already covered by a PO-linked AMC are badged, not blocked.
     */
    const fetchFromPo = async () => {
        if (!poFetchId) { toast.error("Pick a PO first"); return; }
        const po = pos.find((p) => p.id === poFetchId);
        const otherPoRow = form.line_items.find((l) => l._po_id && l._po_id !== poFetchId);
        if (otherPoRow) {
            toast.error(`Items already fetched from PO ${otherPoRow._from_po} — remove them before fetching from another PO`);
            return;
        }
        setPoFetching(true);
        try {
            const r = await api.get(`/billing/amc/po/${poFetchId}/amc-creation-preview`);
            const comps = r.data?.amc_contract?.recurring_components || [];
            if (!comps.length) {
                toast.error("This PO has no recurring/AMC items to fetch");
                return;
            }
            const gstDefault = Math.round(Number(r.data?.amc_contract?.gst_percent));
            const gst = GST_RATES.includes(gstDefault) ? gstDefault : 18;
            const rows = comps.map((c) => blankLine({
                product_name: c.label || "AMC item",
                rate: c.amc_rate_available ? String(c.amount) : "",
                gst_percent: gst,
                _from_po: po?.po_number || poFetchId,
                _po_allocated_to: c.allocated_to?.amc_name || null,
                // Iter C — structured linkage markers (UI-only; translated
                // into po_components on submit, never sent raw).
                _po_id: poFetchId,
                _po_li_id: c.li_id,
                _po_component_code: c.component_code || null,
                _po_source: c.source,
                _po_component_key: c.component_key,
                // Iter E — per-item subscription-end hints (drive the
                // selection-based date suggestion below).
                _sub_end: c.subscription_end_date || null,
                _sub_inv: c.subscription_end_source_invoice || null,
                _sub_conflict: (c.subscription_end_conflicts || []).length > 0,
            }));
            setForm((f) => {
                const existing = f.line_items.filter((l) => (l.product_name || "").trim());
                return {
                    ...f,
                    client_id: po?.client_id || f.client_id,
                    currency: po?.currency || f.currency,
                    amc_name: f.amc_name || `AMC — ${po?.po_number || ""}`.trim(),
                    notes: f.notes || `Items fetched from PO ${po?.po_number || poFetchId}.`,
                    line_items: [...existing, ...rows],
                };
            });
            const autoFreq = r.data?.frequency_resolution?.auto_derived;
            if (autoFreq && FREQUENCIES.includes(autoFreq)) set({ billing_frequency: autoFreq });
            // Iter D (2026-10-14) — carry over the subscription-anchored
            // suggested dates (only when the operator hasn't typed any yet).
            const sug = r.data?.suggested_dates_source;
            setForm((f) => ({
                ...f,
                start_date: f.start_date || r.data?.suggested_start_date || "",
                end_date: f.end_date || r.data?.suggested_end_date || "",
                _po_dates_source: sug || null,
            }));
            toast.success(
                `${rows.length} item${rows.length === 1 ? "" : "s"} fetched from PO ${po?.po_number || ""} — remove any you don't need before saving${sug ? ` · dates suggested from invoice ${sug.source_invoice_number}` : ""}`,
            );
        } catch (err) {
            toast.error(formatApiError(err, "Failed to fetch PO items"));
        } finally {
            setPoFetching(false);
        }
    };

    // Iter E — when PO-fetched rows carry subscription-end hints, derive
    // the suggested dates from the rows currently in the table. Conflicts
    // or mixed ends blank the dates out — the operator must enter them
    // explicitly (never a silent "latest wins").
    const linkedRows = form.line_items.filter((l) => l._po_component_key);
    const linkedConflicts = linkedRows.filter((l) => l._sub_conflict);
    const linkedEnds = [...new Set(linkedRows.map((l) => l._sub_end).filter(Boolean))];
    useEffect(() => {
        if (datesTouched || !linkedRows.length) return;
        if (linkedConflicts.length || linkedEnds.length > 1) {
            setForm((f) => ({ ...f, start_date: "", end_date: "" }));
        } else if (linkedEnds.length === 1) {
            const d = new Date(linkedEnds[0] + "T00:00:00Z"); d.setUTCDate(d.getUTCDate() + 1);
            const start = d.toISOString().slice(0, 10);
            setForm((f) => ({ ...f, start_date: start, end_date: annualPeriodEndIso(start) }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.line_items, datesTouched]);

    // Live totals
    const totals = useMemo(() => {
        let subtotal = 0;
        let gst = 0;
        const rates = new Set();
        for (const l of form.line_items) {
            const amt = (Number(l.quantity) || 0) * (Number(l.rate) || 0);
            const g = (Number(l.gst_percent) || 0) / 100;
            subtotal += amt;
            gst += amt * g;
            if (l.rate) rates.add(Number(l.gst_percent) || 0);
        }
        return {
            subtotal: Math.round(subtotal * 100) / 100,
            gst: Math.round(gst * 100) / 100,
            total: Math.round((subtotal + gst) * 100) / 100,
            mixed: rates.size > 1,
        };
    }, [form.line_items]);

    // DEDUP-AMC-001 (2026-02-15) — Debounced duplicate scan against the
    // proposed combination of (client, annual amount, period). Surfaces a
    // soft-warning panel + override note field when matches are found.
    const [dupCandidates, setDupCandidates] = useState([]);
    const [dupOverrideNote, setDupOverrideNote] = useState("");
    const [dupScanning, setDupScanning] = useState(false);
    /* eslint-disable react-hooks/set-state-in-effect -- debounced API fetch */
    useEffect(() => {
        if (!form.client_id || totals.subtotal <= 0 || !form.start_date || !form.end_date) {
            setDupCandidates([]); return;
        }
        if (new Date(form.end_date) <= new Date(form.start_date)) {
            setDupCandidates([]); return;
        }
        let cancelled = false;
        const t = setTimeout(async () => {
            setDupScanning(true);
            try {
                const r = await api.post("/billing/amc/scan-duplicates", {
                    client_id: form.client_id,
                    annual_amount_excl_gst: totals.subtotal,
                    start_date: form.start_date,
                    end_date: form.end_date,
                });
                if (!cancelled) setDupCandidates(r.data?.candidates || []);
            } catch (_e) {
                if (!cancelled) setDupCandidates([]);
            } finally {
                if (!cancelled) setDupScanning(false);
            }
        }, 450);
        return () => { cancelled = true; clearTimeout(t); };
    }, [form.client_id, totals.subtotal, form.start_date, form.end_date]);
    /* eslint-enable react-hooks/set-state-in-effect */

    const submit = async (e) => {
        e?.preventDefault?.();
        if (!form.client_id) { toast.error("Pick a client"); return; }
        if (!form.amc_name?.trim()) { toast.error("AMC name is required"); return; }
        if (!form.start_date || !form.end_date) { toast.error("Start and End date are required"); return; }
        if (new Date(form.end_date) <= new Date(form.start_date)) {
            toast.error("End date must be after Start date"); return;
        }
        // Validate every line: name + rate (blank or 0 = Included, no charge — same as proposals)
        const cleanLines = form.line_items
            .map((l) => ({ ...l, product_name: (l.product_name || "").trim() }))
            .filter((l) => l.product_name || Number(l.rate) > 0);
        if (cleanLines.length === 0) { toast.error("Add at least one line item"); return; }
        for (const l of cleanLines) {
            if (!l.product_name) { toast.error("Every line needs a product name"); return; }
            if (Number(l.rate) < 0) { toast.error(`Rate cannot be negative for "${l.product_name}"`); return; }
            if (!Number(l.quantity) || Number(l.quantity) <= 0) { toast.error(`Quantity must be > 0 for "${l.product_name}"`); return; }
        }
        if (totals.subtotal <= 0) { toast.error("At least one line must have a rate greater than ₹0 — an AMC cannot have a zero contract value"); return; }
        // Iter C — PO linkage: rows fetched from a PO carry identity markers.
        const linkedRows = cleanLines.filter((l) => l._po_component_key);
        const alreadyAllocated = linkedRows.filter((l) => l._po_allocated_to);
        if (alreadyAllocated.length) {
            toast.error(`"${alreadyAllocated[0].product_name}" is already allocated to ${alreadyAllocated[0]._po_allocated_to} — remove it before saving`);
            return;
        }
        // DEDUP-AMC-001 — Soft override gate.
        if (dupCandidates.length > 0 && !dupOverrideNote.trim()) {
            toast.error(`${dupCandidates.length} possible duplicate AMC${dupCandidates.length === 1 ? "" : "s"} found — provide override reason or adjust the details`);
            return;
        }

        setBusy(true);
        try {
            const payload = {
                client_id: form.client_id,
                amc_name: form.amc_name.trim(),
                start_date: form.start_date,
                end_date: form.end_date,
                billing_frequency: form.billing_frequency,
                notes: form.notes || "",
                // Iter 41: optional client PO reference (UI: "Client PO …",
                // storage: external_po_*). Empty strings dropped to null.
                external_po_number: (form.external_po_number || "").trim() || null,
                external_po_date: (form.external_po_date || "").trim() || null,
                // Iter 49.2: denomination snapshot. Defaults to INR.
                currency: form.currency || "INR",
                // Iter 51.0.2: optional historical REEDOS Proposal No for legacy
                // contracts. Empty / whitespace-only strings normalised to null
                // (backend re-strips defensively too).
                proposal_number: (form.proposal_number || "").trim() || null,
                line_items: cleanLines.map((l) => ({
                    product_name: l.product_name,
                    description: l.description || "",
                    quantity: Number(l.quantity),
                    rate: Number(l.rate) || 0,
                    gst_percent: Number(l.gst_percent),
                    hsn_sac: l.hsn_sac || "",
                    catalogue_id: l.catalogue_id || null,
                })),
            };
            if (form.billing_day) payload.billing_day = Number(form.billing_day);
            if (dupCandidates.length > 0) payload.duplicate_override_note = dupOverrideNote.trim();
            // Iter C — structured linkage when items came from a PO fetch.
            if (linkedRows.length) {
                payload.po_id = linkedRows[0]._po_id;
                payload.po_components = linkedRows.map((l) => ({
                    li_id: l._po_li_id,
                    component_code: l._po_component_code || null,
                    source: l._po_source,
                    rate: Number(l.rate) || 0,
                    included_no_charge: !(Number(l.rate) > 0),
                }));
            }
            const { data } = await api.post("/billing/amc/create-manual", payload);
            toast.success(`Manual AMC scheduled — first invoice on ${data.next_due_date?.slice(0, 10)} · ${data.line_items.length} line item${data.line_items.length !== 1 ? "s" : ""}`);
            baselineRef.current = form;
            onCreated?.(data);
            onOpenChange(false);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to create AMC"));
        } finally {
            setBusy(false);
        }
    };

    // Set of catalogue_ids that the user has already picked into the form.
    // Surfaced as "Already added" badges in the multi-select picker (informational
    // only — operators can still re-pick if a legitimate duplicate is needed).
    const existingCatalogueIds = useMemo(
        () => new Set(form.line_items.map((l) => l.catalogue_id).filter(Boolean)),
        [form.line_items],
    );

    return (
        <FormDialog
            open={open}
            onOpenChange={(v) => !busy && onOpenChange(v)}
            isDirty={isDirty}
            contentClassName="max-w-3xl max-h-[90vh] overflow-y-auto"
            testId="create-manual-amc-dialog"
            header={
                <div className="space-y-1.5">
                    <div className="flex items-center gap-2 text-lg font-semibold">
                        <Repeat size={18} weight="duotone" className="text-amber-600" />
                        Create Manual / Legacy AMC
                    </div>
                    <div className="text-xs text-slate-500">
                        For existing relationships that never went through Proposal → PO.
                        The scheduler will generate <strong>structured invoices</strong> with the line items below — same as a PO-derived AMC.
                        <span className="block mt-1 text-amber-700">
                            Restricted to Super Admin during the initial operational rollout.
                        </span>
                    </div>
                </div>
            }
        >
            <form onSubmit={submit} className="space-y-6 py-2">
                {/* ── Relationship ── */}
                <Section icon={<Buildings size={14} weight="duotone" />} title="Relationship">
                    <Field label="Client *" required wide>
                        <select
                            value={form.client_id}
                            onChange={(e) => set({ client_id: e.target.value })}
                            className="w-full h-9 px-3 rounded border border-slate-200 bg-white text-sm focus:outline-none focus:border-indigo-500"
                            data-testid="manual-amc-client-select"
                        >
                            <option value="">— Pick a client —</option>
                            {clients.map((c) => (
                                <option key={c.id} value={c.id}>
                                    {c.name}{c.legal_name && c.legal_name !== c.name ? ` · ${c.legal_name}` : ""}
                                </option>
                            ))}
                        </select>
                    </Field>
                    <Field label="AMC Name *" required wide hint="A short label shown on the AMC list and the invoice header">
                        <Input
                            value={form.amc_name}
                            onChange={(e) => set({ amc_name: e.target.value })}
                            placeholder="e.g. XIRR.LIVE Annual Maintenance Bundle 2026"
                            data-testid="manual-amc-name-input"
                        />
                    </Field>
                </Section>

                {/* ── Schedule ── */}
                <Section icon={<Calendar size={14} weight="duotone" />} title="Schedule">
                    <Field label="Start Date *" required>
                        <Input type="date" value={form.start_date} onChange={(e) => { setDatesTouched(true); set({ start_date: e.target.value }); }} data-testid="manual-amc-start-date" />
                    </Field>
                    <Field label="End Date *" required>
                        <Input type="date" value={form.end_date} onChange={(e) => { setDatesTouched(true); set({ end_date: e.target.value }); }} data-testid="manual-amc-end-date" />
                    </Field>
                    {linkedConflicts.length > 0 && (
                        <div className="col-span-2 text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-2 py-1.5" data-testid="manual-amc-dates-conflict">
                            Conflicting subscription records for {linkedConflicts.map((l) => l.product_name).join(", ")} — multiple active invoices disagree on the end date. Enter the AMC dates explicitly.
                        </div>
                    )}
                    {linkedConflicts.length === 0 && linkedEnds.length > 1 && (
                        <div className="col-span-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5" data-testid="manual-amc-dates-varied">
                            Fetched items have different subscription end dates — one AMC covers one service period. Remove items or enter the dates explicitly.
                        </div>
                    )}
                    {form._po_dates_source && (
                        <div className="col-span-2 text-[11px] text-sky-800 bg-sky-50 border border-sky-200 rounded-md px-2 py-1.5" data-testid="manual-amc-dates-source">
                            Dates suggested from the current subscription — invoice{" "}
                            <span className="font-mono">{form._po_dates_source.source_invoice_number}</span>
                            {" "}ends on <span className="font-mono">{form._po_dates_source.subscription_end_date}</span>. The AMC starts the next day.
                        </div>
                    )}
                    <Field label="Frequency *" required>
                        <select
                            value={form.billing_frequency}
                            onChange={(e) => set({ billing_frequency: e.target.value })}
                            className="w-full h-9 px-3 rounded border border-slate-200 bg-white text-sm focus:outline-none focus:border-indigo-500"
                            data-testid="manual-amc-frequency"
                        >
                            {FREQUENCIES.map((f) => <option key={f} value={f}>{f}</option>)}
                        </select>
                    </Field>
                    <Field label="Billing Day" hint="Day of month (1–28) for the first invoice. Leave blank to bill on Start Date.">
                        <Input
                            type="number" min={1} max={28}
                            value={form.billing_day}
                            onChange={(e) => set({ billing_day: e.target.value })}
                            placeholder="e.g. 1, 5, 15"
                            data-testid="manual-amc-billing-day"
                        />
                    </Field>
                    {/* Iter 49.2 — currency picker. Defaults to INR; generated
                        invoices inherit this value automatically. */}
                    <Field label="Currency *">
                        <CurrencySelect
                            value={form.currency}
                            onChange={(v) => set({ currency: v })}
                            label=""
                            testid="manual-amc-currency-select"
                        />
                    </Field>
                </Section>

                {/* ── Client PO (external reference) ── Iter 41 ── */}
                <Section icon={<FileText size={14} weight="duotone" />} title="Client PO reference (optional)">
                    <Field label="Client PO Number" hint="The customer's own PO number — used by finance for invoice reconciliation. Distinct from any internal/system PO.">
                        <Input
                            value={form.external_po_number}
                            onChange={(e) => set({ external_po_number: e.target.value })}
                            placeholder="e.g. CLIENT/PO/2026/0042"
                            maxLength={80}
                            data-testid="manual-amc-client-po-number"
                        />
                    </Field>
                    <Field label="Client PO Date">
                        <Input
                            type="date"
                            value={form.external_po_date}
                            onChange={(e) => set({ external_po_date: e.target.value })}
                            data-testid="manual-amc-client-po-date"
                        />
                    </Field>
                    {/* Iter 51.0.2 — historical REEDOS Proposal No, free-text only.
                        Operationally lightweight: typed once at create time,
                        immutable thereafter, no lookup / validation / auto-linking.
                        Solely for legacy contracts onboarded outside the modern
                        Proposal → PO flow. */}
                    <Field
                        label="REEDOS Proposal No (optional)"
                        hint="Historical reference for legacy / pre-digital contracts only — e.g. REE/MFOM/0106/2024-25. Free-text; immutable after creation. Leave blank if not applicable."
                    >
                        <Input
                            value={form.proposal_number}
                            onChange={(e) => set({ proposal_number: e.target.value })}
                            placeholder="e.g. REE/MFOM/0106/2024-25"
                            maxLength={80}
                            data-testid="manual-amc-proposal-number"
                        />
                    </Field>
                </Section>

                {/* ── Line items ── */}
                <div className="space-y-3" data-testid="manual-amc-line-items-section">
                    {/* Iter 165b — fetch recurring items from a PO (unlinked copy) */}
                    <div className="flex items-end gap-2 p-2 rounded-md border border-slate-200 bg-slate-50" data-testid="manual-amc-po-fetch">
                        <div className="flex-1 space-y-1">
                            <Label className="text-xs">Fetch items from PO (optional)</Label>
                            <select
                                value={poFetchId}
                                onChange={(e) => setPoFetchId(e.target.value)}
                                className="w-full h-8 px-2 rounded border border-slate-200 bg-white text-sm focus:outline-none focus:border-indigo-500"
                                data-testid="manual-amc-po-select"
                            >
                                <option value="">Select a finalized PO…</option>
                                {pos.map((p) => (
                                    <option key={p.id} value={p.id}>
                                        {p.po_number || p.id}{p.client?.name ? ` · ${p.client.name}` : ""}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <Button
                            type="button" variant="outline" size="sm"
                            onClick={fetchFromPo}
                            disabled={!poFetchId || poFetching}
                            className="gap-1.5 text-xs"
                            data-testid="manual-amc-po-fetch-btn"
                        >
                            <FileText size={12} /> {poFetching ? "Fetching…" : "Fetch items"}
                        </Button>
                    </div>
                    {form.line_items.some((l) => l._po_component_key) && (() => {
                        const linked = form.line_items.filter((l) => l._po_component_key);
                        return (
                            <div
                                className="text-[11px] text-sky-800 bg-sky-50 border border-sky-200 rounded-md px-2 py-1.5 mb-2"
                                data-testid="manual-amc-po-linkage-chip"
                            >
                                On save, this AMC will be linked to PO {linked[0]._from_po} · {linked.length} item{linked.length === 1 ? "" : "s"} — pool allocation and duplicate-charge checks apply.
                            </div>
                        );
                    })()}
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="reedos-eyebrow flex items-center gap-1.5 text-[10px]">
                                <Receipt size={12} weight="duotone" /> <span>Line Items</span>
                            </div>
                            <div className="text-[11px] text-slate-500 mt-1 leading-snug">
                                Rates capture the <b className="text-slate-700">annual contract value</b> per line.
                                {form.billing_frequency !== "Annual" && (
                                    <> {" "}<span className="text-slate-700">{form.billing_frequency}</span> billing is derived automatically from these annual values.</>
                                )}
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                type="button" variant="outline" size="sm"
                                onClick={() => openCatalogue(null)}
                                className="gap-1.5 text-xs"
                                data-testid="manual-amc-add-from-catalogue-btn"
                            >
                                <MagnifyingGlass size={12} /> Add from catalogue
                            </Button>
                            <Button
                                type="button" variant="outline" size="sm"
                                onClick={addCustomLine}
                                className="gap-1.5 text-xs"
                                data-testid="manual-amc-add-custom-btn"
                            >
                                <Plus size={12} weight="bold" /> Add custom line
                            </Button>
                        </div>
                    </div>

                    <div className="border border-slate-200 rounded-md overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr className="text-left">
                                    <th className="px-3 py-2 reedos-eyebrow text-[10px] w-[26%]">Product / Service</th>
                                    <th className="px-2 py-2 reedos-eyebrow text-[10px] w-[7%] text-right">Qty</th>
                                    <th className="px-2 py-2 reedos-eyebrow text-[10px] w-[18%] text-right">Rate (annual)</th>
                                    <th className="px-2 py-2 reedos-eyebrow text-[10px] w-[9%]">GST</th>
                                    <th className="px-2 py-2 reedos-eyebrow text-[10px] w-[12%]">HSN/SAC</th>
                                    <th className="px-3 py-2 reedos-eyebrow text-[10px] w-[18%] text-right">Annual line total</th>
                                    <th className="px-2 py-2 w-8"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {form.line_items.map((l, idx) => {
                                    // Iter 35: line total is pre-GST (annual value × qty). Footer breaks out
                                    // Subtotal / GST / Total separately, so this row total now matches the
                                    // footer subtotal exactly (no double-counting GST in the visual).
                                    const lineTotal = (Number(l.quantity) || 0) * (Number(l.rate) || 0);
                                    return (
                                        <tr key={l._key} className="border-t border-slate-100 align-top" data-testid={`manual-amc-line-${idx}`}>
                                            <td className="px-3 py-2">
                                                <Input
                                                    value={l.product_name}
                                                    onChange={(e) => updateLine(l._key, { product_name: e.target.value, catalogue_id: null })}
                                                    placeholder="e.g. XIRR.LIVE AMC"
                                                    className="h-8 text-sm"
                                                    data-testid={`manual-amc-line-name-${idx}`}
                                                />
                                                {l.catalogue_id && (
                                                    <div className="text-[9px] text-emerald-700 mt-0.5 font-mono">📌 {l.catalogue_id} (snapshot)</div>
                                                )}
                                                {l._from_po && (
                                                    <div className="text-[9px] text-sky-700 mt-0.5 font-mono" data-testid={`manual-amc-line-frompo-${idx}`}>
                                                        From PO {l._from_po}{l._po_allocated_to ? ` · PO-linked AMC exists: ${l._po_allocated_to}` : ""}
                                                    </div>
                                                )}
                                                <Input
                                                    value={l.description}
                                                    onChange={(e) => updateLine(l._key, { description: e.target.value })}
                                                    placeholder="Optional sub-description"
                                                    className="h-7 mt-1 text-xs text-slate-600"
                                                    data-testid={`manual-amc-line-desc-${idx}`}
                                                />
                                            </td>
                                            <td className="px-2 py-2 text-right">
                                                {/* Iter 35: plain decimal text — no spinner arrows. */}
                                                <Input
                                                    type="text"
                                                    inputMode="decimal"
                                                    value={l.quantity}
                                                    onChange={(e) => updateLine(l._key, { quantity: e.target.value.replace(/[^\d.]/g, "") })}
                                                    className="h-8 text-sm text-right"
                                                    data-testid={`manual-amc-line-qty-${idx}`}
                                                />
                                            </td>
                                            <td className="px-2 py-2 text-right">
                                                <Input
                                                    type="text"
                                                    inputMode="decimal"
                                                    value={l.rate}
                                                    onChange={(e) => updateLine(l._key, { rate: e.target.value.replace(/[^\d.]/g, "") })}
                                                    placeholder="Included"
                                                    className="h-8 text-sm text-right font-mono placeholder:text-emerald-600/70"
                                                    data-testid={`manual-amc-line-rate-${idx}`}
                                                />
                                            </td>
                                            <td className="px-2 py-2">
                                                <select
                                                    value={l.gst_percent}
                                                    onChange={(e) => updateLine(l._key, { gst_percent: Number(e.target.value) })}
                                                    className="w-full h-8 px-2 rounded border border-slate-200 bg-white text-sm focus:outline-none focus:border-indigo-500"
                                                    data-testid={`manual-amc-line-gst-${idx}`}
                                                >
                                                    {GST_RATES.map((r) => <option key={r} value={r}>{r}%</option>)}
                                                </select>
                                            </td>
                                            <td className="px-2 py-2">
                                                <Input
                                                    value={l.hsn_sac}
                                                    onChange={(e) => updateLine(l._key, { hsn_sac: e.target.value })}
                                                    placeholder="e.g. 998314"
                                                    className="h-8 text-sm font-mono"
                                                    data-testid={`manual-amc-line-hsn-${idx}`}
                                                />
                                            </td>
                                            <td className="px-3 py-2 text-right reedos-data font-medium whitespace-nowrap">
                                                {!(Number(l.rate) > 0)
                                                    ? <span className="text-emerald-700 text-[11px] font-semibold" data-testid={`manual-amc-line-included-${idx}`}>Included — ₹0</span>
                                                    : formatINR(lineTotal, form.currency)}
                                            </td>
                                            <td className="px-2 py-2 text-right">
                                                {form.line_items.length > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => removeLine(l._key)}
                                                        className="text-slate-400 hover:text-rose-600 transition-colors"
                                                        data-testid={`manual-amc-line-remove-${idx}`}
                                                        aria-label="Remove line"
                                                    >
                                                        <Trash size={14} />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                            <tfoot className="bg-slate-50 border-t border-slate-200 text-sm" data-testid="manual-amc-totals">
                                <tr>
                                    <td colSpan={5} className="px-3 py-2 text-right text-slate-600">Subtotal · annual</td>
                                    <td className="px-3 py-2 text-right reedos-data font-semibold whitespace-nowrap">{formatINR(totals.subtotal, form.currency)}</td>
                                    <td></td>
                                </tr>
                                <tr>
                                    <td colSpan={5} className="px-3 py-1.5 text-right text-slate-600 text-xs">
                                        GST {totals.mixed && <span className="text-amber-700 font-medium">(Mixed rates)</span>}
                                    </td>
                                    <td className="px-3 py-1.5 text-right reedos-data text-xs whitespace-nowrap">{formatINR(totals.gst, form.currency)}</td>
                                    <td></td>
                                </tr>
                                <tr className="border-t border-slate-200">
                                    <td colSpan={5} className="px-3 py-2 text-right font-semibold text-slate-900">Annual contract value</td>
                                    <td className="px-3 py-2 text-right reedos-data font-bold text-base text-slate-900 whitespace-nowrap" data-testid="manual-amc-total">
                                        {formatINR(totals.total, form.currency)}
                                    </td>
                                    <td></td>
                                </tr>
                                {/* Iter 35: derived periodic value — operators see exactly what each generated invoice will charge. */}
                                {(() => {
                                    const invPerYear = { Annual: 1, "Half-Yearly": 2, Quarterly: 4, Monthly: 12 }[form.billing_frequency] || 1;
                                    if (invPerYear === 1) return null;
                                    const periodic = totals.total / invPerYear;
                                    const freqLabel = form.billing_frequency.toLowerCase();
                                    return (
                                        <tr className="border-t border-slate-100 bg-slate-100/50">
                                            <td colSpan={5} className="px-3 py-2 text-right text-[11px] text-slate-600">
                                                Each {freqLabel} invoice ({invPerYear}/year)
                                            </td>
                                            <td className="px-3 py-2 text-right reedos-data text-slate-700 font-semibold text-sm whitespace-nowrap" data-testid="manual-amc-periodic">
                                                {formatINR(periodic, form.currency)}
                                            </td>
                                            <td></td>
                                        </tr>
                                    );
                                })()}
                            </tfoot>
                        </table>
                    </div>
                </div>

                {/* ── Notes ── */}
                <div>
                    <Label className="text-[11px] reedos-eyebrow text-slate-600">Notes</Label>
                    <textarea
                        rows={2}
                        value={form.notes}
                        onChange={(e) => set({ notes: e.target.value })}
                        className="w-full mt-1 px-3 py-2 rounded border border-slate-200 bg-white text-sm focus:outline-none focus:border-indigo-500"
                        placeholder="Internal — won't appear on the invoice. Useful for legacy contract refs (e.g. Migrated from M-2024-007)"
                        data-testid="manual-amc-notes"
                    />
                </div>

                {/* DEDUP-AMC-001 (2026-02-15) — Possible duplicate AMC warning.
                    Renders whenever the live scan returns >=1 candidates.
                    Super-admin can proceed with a one-line override note,
                    audit-logged on the new schedule. */}
                {dupCandidates.length > 0 && (
                    <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2.5 space-y-1.5 mt-3" data-testid="manual-amc-duplicate-warning">
                        <div className="flex items-center gap-1.5">
                            <Receipt size={14} className="text-rose-700" weight="fill" />
                            <div className="font-bold text-[11px] uppercase tracking-wider text-rose-900">
                                {dupCandidates.length} possible duplicate AMC{dupCandidates.length === 1 ? "" : "s"} detected
                            </div>
                        </div>
                        <ul className="text-[11px] text-rose-900 space-y-0.5 max-h-32 overflow-y-auto pl-1">
                            {dupCandidates.slice(0, 6).map((c) => (
                                <li key={c.schedule_id} data-testid={`manual-dup-candidate-${c.schedule_id}`} className="flex items-start gap-1.5">
                                    <span className="font-mono text-[10px] text-rose-700">·</span>
                                    <div className="flex-1 min-w-0">
                                        <span className="font-semibold">{c.amc_name}</span>
                                        <span className="text-rose-700"> · {c.client_name}</span>
                                        <span className="text-rose-700"> · {c.billing_frequency} · {formatINR(c.annualised_excl_gst, form.currency)}/yr</span>
                                        {!c.same_client && c.same_gstin && (
                                            <span className="ml-1 inline-block rounded bg-rose-100 px-1 py-0 text-[9px] font-bold">same GSTIN, different client</span>
                                        )}
                                    </div>
                                </li>
                            ))}
                            {dupCandidates.length > 6 && (
                                <li className="text-rose-700 italic pl-3">… and {dupCandidates.length - 6} more</li>
                            )}
                        </ul>
                        <p className="text-[11px] text-rose-800">
                            Super Admin may proceed if this is a legitimate separate contract. Provide a written reason for audit.
                        </p>
                        <textarea
                            rows={2}
                            value={dupOverrideNote}
                            onChange={(e) => setDupOverrideNote(e.target.value)}
                            placeholder="Reason for creating despite possible duplicate (e.g. parent + subsidiary on same GSTIN)…"
                            className="w-full px-3 py-2 rounded border border-rose-300 bg-white text-sm focus:outline-none focus:border-rose-500"
                            data-testid="manual-amc-duplicate-override-input"
                            maxLength={2000}
                        />
                    </div>
                )}
                {dupScanning && (
                    <p className="text-[10px] text-slate-500 mt-1 ml-1" data-testid="manual-amc-duplicate-scan-progress">
                        Checking for duplicates…
                    </p>
                )}

                <DialogFooter className="border-t border-slate-100 -mx-6 px-6 pt-4 mt-2">
                    <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid="manual-amc-cancel-btn">
                        Cancel
                    </Button>
                    <Button type="submit" disabled={busy} data-testid="manual-amc-create-btn" className="bg-amber-600 hover:bg-amber-500 text-white">
                        {busy ? "Creating…" : "Create Manual AMC"}
                    </Button>
                </DialogFooter>
            </form>

            {/* ── Catalogue picker (shared multi-select component, 2026-02-13) ── */}
            <CataloguePickerDialog
                open={pickerOpen}
                onOpenChange={(v) => {
                    setPickerOpen(v);
                    if (!v) setPickerLineKey(null);
                }}
                catalogue={catalogue}
                existingCatalogueIds={existingCatalogueIds}
                formatPrice={(n) => formatINR(n, form.currency)}
                onAddSelected={commitCatalogueSelection}
            />
        </FormDialog>
    );
}

function Section({ icon, title, children }) {
    return (
        <div className="space-y-3">
            <div className="reedos-eyebrow flex items-center gap-1.5 text-[10px]">
                {icon}<span>{title}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>
        </div>
    );
}

function Field({ label, hint, required, wide, children }) {
    return (
        <div className={wide ? "sm:col-span-2" : ""}>
            <Label className="text-[11px] reedos-eyebrow text-slate-600">
                {label}
                {required && <span className="text-rose-500 ml-0.5">*</span>}
            </Label>
            <div className="mt-1">{children}</div>
            {hint && <div className="text-[10px] text-slate-500 mt-1 leading-snug">{hint}</div>}
        </div>
    );
}
